import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Home as HomeIcon, Building2, Hammer, Instagram } from "lucide-react";
import Testimonials from "@/components/site/Testimonials";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}>
      {children}
    </motion.span>
  </span>
);

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 36 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-70px" }} transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
    className={className}>
    {children}
  </motion.div>
);

const SERVICES = [
  {
    icon: HomeIcon, title: "Residential Builds",
    text: "Architectural new homes, thoughtful renovations and our own plan range — from compact 50m² dwellings to designer family homes. Built by our Masterton crew, start to finish.",
    points: ["New custom homes", "Plan range — 13 designs", "Architectural new builds", "Minor dwellings & prefab"],
  },
  {
    icon: Building2, title: "Commercial Builds",
    text: "Fit-outs, workshops and commercial spaces delivered on programme and on budget — with the same finish standard we put into our homes.",
    points: ["Commercial fit-outs", "Steel & timber structures", "Hospitality & retail", "Project management"],
  },
  {
    icon: Hammer, title: "Renovations",
    text: "From full home transformations to kitchen and bathroom upgrades — no job is too big or too small. LW&Co brings the same quality and craftsmanship to every renovation project.",
    points: ["Full home transformations", "Kitchen & bathroom upgrades", "Extensions & additions", "No job too big or small"],
  },
];

const FEATURED = [
  { to: "/our-builds", img: "/images/real-rural-home.jpg", badge: null, title: "Rural Family Home", meta: "Residential — Wairarapa", testid: "teaser-rural", span: "md:col-span-7" },
  { to: "/our-builds", img: "/images/real-commercial-interior.jpg", badge: null, title: "Commercial Shed Build", meta: "Commercial — Wairarapa", testid: "teaser-commercial", span: "md:col-span-5" },
];

const INSTA_SHOTS = [
  "/images/real-rural-home.jpg",
  "/images/real-commercial-interior.jpg",
  "/images/real-concrete-pour.jpg",
  "/images/real-steel-frame.jpg",
  "/images/real-commercial-site.jpg",
];

export default function Home() {
  return (
    <main data-testid="home-page">
      {/* HERO — real rural residential photo */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-end overflow-hidden bg-black">
        <motion.img initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
          src="/images/real-rural-home.jpg" alt="LW&Co rural home — dark cladding beneath the Wairarapa ranges"
          className="absolute inset-0 w-full h-full object-cover" data-testid="hero-image" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/60" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pb-20 sm:pb-28 pt-44 w-full">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25, duration: 0.8 }}
            className="text-[11px] font-bold uppercase tracking-[0.3em] text-gold mb-6" data-testid="hero-eyebrow">
            LW&Co Building Company Ltd — Inc. 2017
          </motion.p>
          <h1 className="font-display font-semibold uppercase text-white leading-[0.95] tracking-tight text-5xl sm:text-7xl lg:text-8xl"
            data-testid="hero-headline">
            <MaskedLine delay={0.35}>We build</MaskedLine>
            <MaskedLine delay={0.5}>exceptional</MaskedLine>
            <MaskedLine delay={0.65}>homes.</MaskedLine>
          </h1>
          <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ delay: 1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="origin-left h-[3px] w-28 bg-gold mt-8" />
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.05, duration: 0.8 }}
            className="mt-6 text-base sm:text-lg text-white/80 font-medium tracking-wide" data-testid="hero-subtext">
            Residential &amp; Commercial | Masterton, Wairarapa
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.8 }}
            className="mt-10 flex flex-wrap gap-4">
            <Link to="/our-builds" data-testid="hero-builds-btn"
              className="group inline-flex items-center gap-2 px-8 py-4 bg-gold text-black font-bold text-sm uppercase tracking-[0.15em] hover:bg-white transition-colors duration-300">
              See Our Builds <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/our-plans" data-testid="hero-plans-btn"
              className="inline-flex items-center px-8 py-4 border border-white/40 text-white font-bold text-sm uppercase tracking-[0.15em] hover:border-gold hover:text-gold transition-colors duration-300">
              Browse Plan Range
            </Link>
          </motion.div>
        </div>
      </section>

      {/* COMPANY INTRO */}
      <section data-testid="about-snippet" className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-7">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-gold mb-6">The company</p>
            <h2 className="font-display font-semibold uppercase leading-[1.02] tracking-tight text-4xl sm:text-5xl lg:text-6xl">
              Local crew. Straight talk. Sharp finish.
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5 flex flex-col justify-end">
            <p className="text-base sm:text-lg text-black/60 leading-relaxed">
              Incorporated in 2017 and based in Masterton, LW&Co Building Company Limited builds
              homes and commercial spaces the way we'd want them built for ourselves — honest
              pricing, clear communication and craftsmanship that holds up for decades.
            </p>
            <div className="mt-8 grid grid-cols-3 gap-6 border-t border-black/10 pt-8" data-testid="about-stats">
              {[["2017", "Incorporated"], ["R + C", "Residential & Commercial"], ["100%", "Wairarapa Local"]].map(([v, l]) => (
                <div key={l}>
                  <div className="font-display font-semibold text-2xl sm:text-3xl">{v}</div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-black/40 mt-1.5">{l}</div>
                </div>
              ))}
            </div>
            <Link to="/about" data-testid="about-snippet-link"
              className="group mt-8 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-black hover:text-gold transition-colors w-fit">
              Our story <ArrowUpRight size={16} className="text-gold group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section data-testid="services-section" className="py-24 sm:py-32 bg-white border-t border-black/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal className="mb-14">
            <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-gold mb-6">What we do</p>
            <h2 className="font-display font-semibold uppercase leading-[1.02] tracking-tight text-4xl sm:text-5xl">
              Three disciplines. One standard.
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-px bg-black/10 border border-black/10">
            {SERVICES.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.12}>
                <article data-testid={`service-card-${i + 1}`} className="group bg-white p-8 sm:p-12 h-full hover:bg-black transition-colors duration-500">
                  <s.icon size={30} className="text-gold" strokeWidth={1.5} />
                  <h3 className="font-display font-semibold uppercase text-2xl sm:text-3xl tracking-tight mt-6 group-hover:text-white transition-colors duration-500">
                    {s.title}
                  </h3>
                  <p className="mt-4 text-sm sm:text-base text-black/55 group-hover:text-white/60 leading-relaxed transition-colors duration-500">
                    {s.text}
                  </p>
                  <ul className="mt-8 space-y-2.5 border-t border-black/10 group-hover:border-white/15 pt-6 transition-colors duration-500">
                    {s.points.map((p) => (
                      <li key={p} className="text-[11px] font-bold uppercase tracking-[0.18em] text-black/40 group-hover:text-gold flex items-center gap-2.5 transition-colors duration-500">
                        <span className="w-1 h-1 bg-gold" />{p}
                      </li>
                    ))}
                  </ul>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED BUILDS — real photography */}
      <section data-testid="featured-builds" className="bg-black text-white py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-gold mb-6">Featured builds</p>
              <h2 className="font-display font-semibold uppercase leading-[1.02] tracking-tight text-4xl sm:text-5xl">
                Fresh from the tools.
              </h2>
            </div>
            <Link to="/our-builds" data-testid="featured-builds-link"
              className="group inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] text-white/70 hover:text-gold transition-colors">
              All builds <ArrowUpRight size={16} className="text-gold" />
            </Link>
          </Reveal>
          <div className="grid md:grid-cols-12 gap-5">
            {FEATURED.map((f, i) => (
              <Reveal key={f.testid} delay={i * 0.12} className={f.span}>
                <Link to={f.to} data-testid={f.testid} className="group block relative overflow-hidden h-full">
                  <img src={f.img} alt={f.title}
                    className="w-full h-full min-h-[320px] object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent" />
                  {f.badge && <span className="absolute top-5 left-5 px-4 py-1.5 bg-gold text-black text-[10px] font-bold uppercase tracking-[0.25em]">{f.badge}</span>}
                  <div className="absolute bottom-0 inset-x-0 p-7">
                    <h3 className="font-display font-semibold uppercase text-2xl sm:text-3xl tracking-tight">{f.title}</h3>
                    <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.25em] text-white/60">{f.meta}</p>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FOLLOW THE BUILD */}
      <section data-testid="follow-the-build" className="bg-white py-20 sm:py-24 border-t border-black/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 flex flex-wrap items-center justify-between gap-10">
          <Reveal className="max-w-2xl">
            <h2 className="font-display font-semibold uppercase leading-[1.02] tracking-tight text-4xl sm:text-5xl">
              Follow the build.
            </h2>
            <div className="h-[3px] w-20 bg-gold mt-6" />
            <p className="mt-6 text-base sm:text-lg text-black/60 leading-relaxed" data-testid="follow-build-text">
              Watch our builds come to life — follow @lwandco on Instagram for real-time updates from every project.
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <a href="https://www.instagram.com/lwandco" target="_blank" rel="noopener noreferrer" data-testid="follow-build-link"
              className="group inline-flex items-center gap-3 px-9 py-4 bg-black text-white font-bold text-sm uppercase tracking-[0.15em] hover:bg-gold hover:text-black transition-colors duration-300">
              <Instagram size={17} /> @lwandco <ArrowUpRight size={16} className="text-gold group-hover:text-black transition-colors" />
            </a>
          </Reveal>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Testimonials />

      {/* INSTAGRAM — real site photography */}
      <section data-testid="instagram-section" className="py-24 sm:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <Reveal className="flex flex-wrap items-end justify-between gap-6 mb-12">
            <h2 className="font-display font-semibold uppercase leading-none tracking-tight text-4xl sm:text-5xl">
              Follow the build
            </h2>
            <a href="https://www.instagram.com/lwandco" target="_blank" rel="noopener noreferrer" data-testid="instagram-profile-link"
              className="group inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.15em] hover:text-gold transition-colors">
              @lwandco <ArrowUpRight size={16} className="text-gold" />
            </a>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {INSTA_SHOTS.map((src, i) => (
              <motion.a key={src} href="https://www.instagram.com/lwandco" target="_blank" rel="noopener noreferrer"
                data-testid={`instagram-tile-${i + 1}`}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: i * 0.08 }}
                className={`group relative overflow-hidden ${i === 0 ? "col-span-2 row-span-2" : "aspect-square"}`}>
                <img src={src} alt="LW&Co on the tools — Instagram" loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/35 transition-colors duration-500 flex items-center justify-center">
                  <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-500 text-white text-[10px] font-bold uppercase tracking-[0.25em]">
                    @lwandco
                  </span>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section data-testid="contact-cta" className="bg-white py-28 sm:py-36 border-t border-black/10">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
          <Reveal>
            <h2 className="font-display font-semibold uppercase leading-[0.95] tracking-tight text-5xl sm:text-7xl">
              Let's build yours.
            </h2>
            <div className="h-[3px] w-24 bg-gold mx-auto mt-8" />
            <p className="mt-8 max-w-lg mx-auto text-base text-black/55 leading-relaxed">
              New home, renovation or commercial project — tell us what you're planning and we'll take it from there.
            </p>
            <Link to="/contact" data-testid="cta-enquire-btn"
              className="mt-10 inline-flex items-center gap-2 px-10 py-4 bg-black text-white font-bold text-sm uppercase tracking-[0.15em] hover:bg-gold hover:text-black transition-colors duration-300">
              Start an enquiry <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
